/**
 * Google Drive File/Folder Picker Component
 * Allows users to browse and select files/folders to sync
 */

import { useState, useEffect } from 'react';
import {
  Folder,
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  X,
  Loader2,
  Home,
} from 'lucide-react';
import { api } from '../lib/api';

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  size?: string;
}

interface GoogleDrivePickerProps {
  onClose: () => void;
  onSave: (selectedFiles: DriveFile[]) => void;
  initialSelected?: DriveFile[];
  sourceType?: 'google_drive' | 'google_meet';
}

export function GoogleDrivePicker({ onClose, onSave, initialSelected = [], sourceType = 'google_drive' }: GoogleDrivePickerProps) {
  const [folders, setFolders] = useState<DriveFile[]>([]);
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<Array<{ id: string; name: string }>>([]);

  // Use a plain object for selection state (React handles object changes better than Map)
  // Object shape: { [fileId]: DriveFile }
  const [selectedById, setSelectedById] = useState<Record<string, DriveFile>>(() => {
    const initial: Record<string, DriveFile> = {};
    for (const file of initialSelected) {
      initial[file.id] = file;
    }
    console.log('[GoogleDrivePicker] Initialized with', Object.keys(initial).length, 'files');
    return initial;
  });

  // For google_meet, auto-navigate to Meet Recordings folder
  const [meetFolderResolved, setMeetFolderResolved] = useState(sourceType !== 'google_meet');

  useEffect(() => {
    if (sourceType !== 'google_meet') return;
    let cancelled = false;
    (async () => {
      try {
        const result = await api.findMeetRecordingsFolder();
        if (!cancelled && result.data?.data?.folderId) {
          const folderName = result.data.data.folderName || 'Meet Recordings';
          setCurrentPath([{ id: result.data.data.folderId, name: folderName }]);
        }
      } catch {
        // Folder not found — fall back to Drive root
      }
      if (!cancelled) setMeetFolderResolved(true);
    })();
    return () => { cancelled = true; };
  }, [sourceType]);

  const currentFolderId = currentPath.length > 0 ? currentPath[currentPath.length - 1].id : undefined;

  // Load files when folder changes (wait for Meet folder resolution)
  useEffect(() => {
    if (!meetFolderResolved) return;
    loadFiles(currentFolderId);
  }, [currentFolderId, meetFolderResolved]);

  const loadFiles = async (folderId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listGoogleDriveFiles(folderId, sourceType);
      if (result.data?.data) {
        setFolders(result.data.data.folders);
        setFiles(result.data.data.files);
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch {
      setError('Failed to load files');
    }
    setLoading(false);
  };

  const navigateToFolder = (folder: DriveFile) => {
    setCurrentPath([...currentPath, { id: folder.id, name: folder.name }]);
  };

  const navigateBack = () => {
    setCurrentPath(currentPath.slice(0, -1));
  };

  const navigateToRoot = () => {
    setCurrentPath([]);
  };

  const navigateToBreadcrumb = (index: number) => {
    setCurrentPath(currentPath.slice(0, index + 1));
  };

  // Toggle file selection - simplified without useCallback to avoid stale closures
  const toggleFileSelection = (file: DriveFile, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setSelectedById(prev => {
      const isCurrentlySelected = file.id in prev;
      console.log('[GoogleDrivePicker] Toggle file:', file.name, 'was selected:', isCurrentlySelected, '-> will be:', !isCurrentlySelected);

      if (isCurrentlySelected) {
        // Remove from selection
        const { [file.id]: _, ...rest } = prev;
        return rest;
      } else {
        // Add to selection
        return { ...prev, [file.id]: file };
      }
    });
  };

  // Toggle folder selection - simplified without useCallback to avoid stale closures
  const toggleFolderSelection = (folder: DriveFile, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setSelectedById(prev => {
      const isCurrentlySelected = folder.id in prev;
      console.log('[GoogleDrivePicker] Toggle folder:', folder.name, 'was selected:', isCurrentlySelected, '-> will be:', !isCurrentlySelected);

      if (isCurrentlySelected) {
        // Remove from selection
        const { [folder.id]: _, ...rest } = prev;
        return rest;
      } else {
        // Add to selection
        return { ...prev, [folder.id]: folder };
      }
    });
  };

  const handleSave = () => {
    const selectedArray = Object.values(selectedById);
    console.log('[GoogleDrivePicker] Saving', selectedArray.length, 'items');
    onSave(selectedArray);
  };

  const isTextFile = (mimeType: string) => {
    return (
      mimeType.includes('text') ||
      mimeType.includes('document') ||
      mimeType.includes('sheet') ||
      mimeType.includes('presentation') ||
      mimeType === 'application/pdf'
    );
  };

  const formatFileSize = (size?: string) => {
    if (!size) return '';
    const bytes = parseInt(size, 10);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Helper to check if an item is selected
  const isSelected = (id: string) => id in selectedById;

  // Count of selected items
  const selectedCount = Object.keys(selectedById).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {sourceType === 'google_meet' ? 'Select Meet Recordings' : 'Select Files & Folders'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-1 text-sm overflow-x-auto">
          <button
            onClick={navigateToRoot}
            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-200"
          >
            <Home className="h-4 w-4" />
            <span>{sourceType === 'google_meet' ? 'Meet Recordings' : 'My Drive'}</span>
          </button>
          {currentPath.map((item, index) => (
            <span key={item.id} className="flex items-center">
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <button
                onClick={() => navigateToBreadcrumb(index)}
                className="px-2 py-1 rounded hover:bg-gray-200 truncate max-w-[150px]"
              >
                {item.name}
              </button>
            </span>
          ))}
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-mazo-600" />
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-600">{error}</div>
          ) : folders.length === 0 && files.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>This folder is empty</p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Back button when in subfolder */}
              {currentPath.length > 0 && (
                <button
                  onClick={navigateBack}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">Back</span>
                </button>
              )}

              {/* Folders */}
              {folders.map((folder) => {
                const folderIsSelected = isSelected(folder.id);
                return (
                  <div
                    key={folder.id}
                    className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50"
                  >
                    <button
                      type="button"
                      onClick={(e) => toggleFolderSelection(folder, e)}
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        folderIsSelected
                          ? 'bg-mazo-600 border-mazo-600 text-white'
                          : 'border-gray-300 hover:border-mazo-400'
                      }`}
                    >
                      {folderIsSelected && <Check className="h-3 w-3" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigateToFolder(folder)}
                      className="flex-1 flex items-center gap-3 text-left"
                    >
                      <Folder className="h-5 w-5 text-yellow-500" />
                      <span className="font-medium truncate">{folder.name}</span>
                      <ChevronRight className="h-4 w-4 text-gray-400 ml-auto" />
                    </button>
                  </div>
                );
              })}

              {/* Files */}
              {files.map((file) => {
                const fileIsSelected = isSelected(file.id);
                const canSelect = isTextFile(file.mimeType);
                return (
                  <button
                    type="button"
                    key={file.id}
                    onClick={(e) => canSelect && toggleFileSelection(file, e)}
                    disabled={!canSelect}
                    className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                      canSelect ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        fileIsSelected
                          ? 'bg-mazo-600 border-mazo-600 text-white'
                          : 'border-gray-300'
                      }`}
                    >
                      {fileIsSelected && <Check className="h-3 w-3" />}
                    </div>
                    <FileText className="h-5 w-5 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{file.name}</div>
                      <div className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={selectedCount === 0}
              className="px-4 py-2 bg-mazo-600 text-white rounded-lg hover:bg-mazo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
